/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useEffect, useMemo, useState } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import AtomIcon from '@atlaskit/icon-lab/core/atom';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import CloudArrowUpIcon from '@atlaskit/icon/core/cloud-arrow-up';
import EditBulkIcon from '@atlaskit/icon/core/edit-bulk';
import PersonAddedIcon from '@atlaskit/icon/core/person-added';
import SearchIcon from '@atlaskit/icon/core/search';
import SettingsIcon from '@atlaskit/icon/core/settings';
import { fg } from '@atlaskit/platform-feature-flags';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import {
	type AIActionType,
	type ConversationAction,
	type ConversationStarterLabelType,
	type ConversationStarterWithLabel,
	HandleActionType,
	type NestedAction,
	PageContextType,
} from '../../common/types';
import { useWindowLocation } from '../../common/utils/use-window-location';
import { useFetchConvoStarters } from '../../services/fetch-convo-starters';

import messages from './messages';

export const pageTypeConversationStarters: {
	[key in PageContextType]: Array<{
		labelType: ConversationStarterLabelType;
		message: MessageDescriptor;
	}>;
} = {
	[PageContextType.BITBUCKET_CREATE_PULL_REQUEST]: [
		{ labelType: 'find-people', message: messages.bitbucketCreatePullRequestsPrompt1 },
	],
	[PageContextType.BITBUCKET_PULL_REQUEST]: [
		{ labelType: 'summarize-information', message: messages.bitbucketPullRequestsPrompt1 },
		{ labelType: 'summarize-code', message: messages.bitbucketPullRequestsPrompt4 },
		{ labelType: 'analyze-content', message: messages.bitbucketPullRequestsPrompt3 },
		{ labelType: 'get-answers', message: messages.bitbucketPullRequestsPrompt5 },
	],
	[PageContextType.BITBUCKET_PULL_REQUEST_WITHOUT_PIPELINES_STARTERS]: [
		{ labelType: 'summarize-information', message: messages.bitbucketPullRequestsPrompt1 },
		{ labelType: 'summarize-code', message: messages.bitbucketPullRequestsPrompt4 },
		{ labelType: 'analyze-content', message: messages.bitbucketPullRequestsPrompt3 },
	],
	[PageContextType.BITBUCKET_REPOSITORY_SOURCE]: [
		{ labelType: 'summarize-code', message: messages.bitbucketRepositorySourcePrompt1 },
		{ labelType: 'find-information', message: messages.bitbucketRepositorySourcePrompt4 },
		{ labelType: 'code-search', message: messages.bitbucketRepositorySourcePrompt5 },
	],
	[PageContextType.BITBUCKET_PIPELINES]: [
		{ labelType: 'get-answers', message: messages.bitbucketPipelinePrompt1 },
	],
	[PageContextType.BITBUCKET]: [
		{ labelType: 'find-information', message: messages.bitbucketPrompt5 },
		{ labelType: 'check-status', message: messages.bitbucketPrompt6 },
		{ labelType: 'find-people', message: messages.bitbucketPrompt7 },
	],
	[PageContextType.CONFLUENCE_HOME]: [
		{ labelType: 'be-productive', message: messages.confluenceHomePrompt1 },
		{ labelType: 'stay-up-to-date', message: messages.confluenceHomePrompt2 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt3 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt4 },
		{ labelType: 'rephrase-content', message: messages.confluenceHomePrompt5 },
		{ labelType: 'draft-content', message: messages.confluenceHomePrompt6 },
		{ labelType: 'find-information', message: messages.confluenceHomePrompt7 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt8 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt9 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt10 },
		{ labelType: 'be-productive', message: messages.confluenceHomePrompt11 },
	],
	[PageContextType.CONFLUENCE_PAGE_VIEW]: [
		{ labelType: 'summarize-content', message: messages.confluencePageViewPrompt1 },
		{ labelType: 'summarize-content', message: messages.confluencePageViewPrompt2 },
		{ labelType: 'rephrase-content', message: messages.confluencePageViewPrompt3 },
		{ labelType: 'find-people', message: messages.confluencePageViewPrompt4 },
		{ labelType: 'stay-up-to-date', message: messages.confluencePageViewPrompt5 },
		{ labelType: 'be-productive', message: messages.confluencePageViewPrompt6 },
	],
	[PageContextType.CONFLUENCE_BLOG_VIEW]: [
		{ labelType: 'summarize-content', message: messages.confluencePageViewPrompt1 },
		{ labelType: 'summarize-content', message: messages.confluencePageViewPrompt2 },
		{ labelType: 'rephrase-content', message: messages.confluencePageViewPrompt3 },
		{ labelType: 'find-people', message: messages.confluencePageViewPrompt4 },
		{ labelType: 'stay-up-to-date', message: messages.confluencePageViewPrompt5 },
		{ labelType: 'be-productive', message: messages.confluencePageViewPrompt6 },
	],
	[PageContextType.CONFLUENCE_WHITEBOARD]: [
		{ labelType: 'summarize-content', message: messages.confluenceWhiteboardPrompt1 },
		{ labelType: 'get-answers', message: messages.confluenceWhiteboardPrompt2 },
		{ labelType: 'find-information', message: messages.confluenceWhiteboardPrompt3 },
		{ labelType: 'be-productive', message: messages.confluenceWhiteboardPrompt4 },
	],
	[PageContextType.CONFLUENCE_PAGE_EDIT]: [
		{ labelType: 'rephrase-content', message: messages.confluencePageEditPrompt1 },
		{ labelType: 'draft-content', message: messages.confluencePageEditPrompt2 },
		{ labelType: 'find-information', message: messages.confluencePageEditPrompt3 },
		{ labelType: 'be-productive', message: messages.confluencePageEditPrompt4 },
	],
	[PageContextType.CONFLUENCE_BLOG_EDIT]: [
		{ labelType: 'rephrase-content', message: messages.confluencePageEditPrompt1 },
		{ labelType: 'draft-content', message: messages.confluencePageEditPrompt2 },
		{ labelType: 'find-information', message: messages.confluencePageEditPrompt3 },
		{ labelType: 'be-productive', message: messages.confluencePageEditPrompt4 },
	],
	[PageContextType.JIRA_CMP_SPACE_SETTINGS_PEOPLE_LEGACY]: [],
	[PageContextType.JIRA_CMP_SPACE_SETTINGS_PERMISSIONS_LEGACY]: [],
	[PageContextType.JIRA_TMP_SPACE_SETTINGS_ACCESS]: [],
	[PageContextType.JIRA_HOME]: [
		{ labelType: 'stay-up-to-date', message: messages.jiraHomePrompt1 },
		{ labelType: 'be-productive', message: messages.jiraHomePrompt2 },
		{ labelType: 'find-information', message: messages.jiraHomePrompt3 },
		{ labelType: 'summarize-content', message: messages.jiraHomePrompt4 },
		{ labelType: 'draft-content', message: messages.jiraHomePrompt5 },
		{ labelType: 'be-productive', message: messages.jiraHomePrompt6 },
	],
	[PageContextType.JIRA_FOR_YOU]: [
		{ labelType: 'find-information', message: messages.unknownPrompt1 },
		{ labelType: 'be-productive', message: messages.unknownPrompt2 },
		{ labelType: 'draft-content', message: messages.unknownPrompt3 },
	],
	[PageContextType.JIRA_ISSUE_VIEW]: [
		{ labelType: 'find-information', message: messages.jiraIssueViewPrompt1 },
		{ labelType: 'rephrase-content', message: messages.jiraIssueViewPrompt2 },
		{ labelType: 'draft-content', message: messages.jiraIssueViewPrompt3 },
		{ labelType: 'be-productive', message: messages.jiraIssueViewPrompt4 },
	],
	[PageContextType.JIRA_PROJECT]: [
		{ labelType: 'stay-up-to-date', message: messages.jiraProjectPrompt1 },
		{ labelType: 'be-productive', message: messages.jiraProjectPrompt2 },
		{ labelType: 'measure-productivity', message: messages.jiraProjectPrompt3 },
		{ labelType: 'find-people', message: messages.jiraProjectPrompt4 },
		{ labelType: 'find-people', message: messages.jiraProjectPrompt5 },
		{ labelType: 'be-productive', message: messages.jiraProjectPrompt6 },
		{ labelType: 'measure-productivity', message: messages.jiraProjectPrompt7 },
		{ labelType: 'find-people', message: messages.jiraProjectPrompt8 },
		{ labelType: 'find-information', message: messages.jiraProjectPrompt9 },
		{ labelType: 'get-answers', message: messages.jiraProjectPrompt10 },
		{ labelType: 'rephrase-content', message: messages.jiraProjectPrompt11 },
		{ labelType: 'summarize-content', message: messages.jiraProjectPrompt12 },
		{ labelType: 'draft-content', message: messages.jiraProjectPrompt13 },
		{ labelType: 'be-productive', message: messages.jiraProjectPrompt14 },
	],
	[PageContextType.JSM_INCIDENT_VIEW]: [
		{ labelType: 'find-people', message: messages.jsmIncidentViewPrompt1 },
		{ labelType: 'stay-up-to-date', message: messages.jsmIncidentViewPrompt2 },
		{ labelType: 'draft-content', message: messages.jsmIncidentViewPrompt3 },
	],
	[PageContextType.JSM_INCIDENT_LIST_VIEW]: [
		{ labelType: 'stay-up-to-date', message: messages.jsmIncidentListViewPrompt1 },
		{ labelType: 'get-answers', message: messages.jsmIncidentListViewPrompt2 },
		{ labelType: 'find-information', message: messages.jsmIncidentListViewPrompt3 },
	],
	[PageContextType.JSM_ALERT_VIEW]: [
		{ labelType: 'find-information', message: messages.jsmAlertViewPrompt1 },
		{ labelType: 'get-answers', message: messages.jsmAlertViewPrompt2 },
		{ labelType: 'stay-up-to-date', message: messages.jsmAlertViewPrompt3 },
	],
	[PageContextType.JSM_ALERT_LIST_VIEW]: [
		{ labelType: 'find-information', message: messages.jsmAlertListViewPrompt1 },
		{ labelType: 'get-answers', message: messages.jsmAlertListViewPrompt2 },
		{ labelType: 'stay-up-to-date', message: messages.jsmAlertListViewPrompt3 },
	],
	[PageContextType.JSM_ALERT_GROUP_DETAIL_VIEW]: [
		{ labelType: 'find-information', message: messages.jsmAlertGroupDetailViewPrompt1 },
		{ labelType: 'get-answers', message: messages.jsmAlertGroupDetailViewPrompt2 },
		{ labelType: 'stay-up-to-date', message: messages.jsmAlertGroupDetailViewPrompt3 },
	],
	[PageContextType.ATLAS_HOME]: [
		{ labelType: 'find-information', message: messages.atlasHomePrompt1 },
		{ labelType: 'measure-productivity', message: messages.atlasHomePrompt2 },
		{ labelType: 'get-answers', message: messages.atlasHomePrompt3 },
		{ labelType: 'draft-content', message: messages.atlasHomePrompt4 },
		{ labelType: 'be-productive', message: messages.atlasHomePrompt5 },
		{ labelType: 'draft-content', message: messages.atlasHomePrompt6 },
		{ labelType: 'be-productive', message: messages.confluenceHomePrompt1 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt4 },
		{ labelType: 'rephrase-content', message: messages.confluenceHomePrompt5 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt11 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt8 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt10 },
		{ labelType: 'stay-up-to-date', message: messages.jiraHomePrompt2 },
		{ labelType: 'find-information', message: messages.jiraHomePrompt3 },
		{ labelType: 'summarize-content', message: messages.jiraHomePrompt4 },
		{ labelType: 'be-productive', message: messages.atlasHomePrompt7 },
	],
	[PageContextType.ATLAS_HOME_MAIN]: [],
	[PageContextType.ATLAS_HOME_DISCOVER]: [],
	[PageContextType.ATLAS_HOME_RECENTS]: [],
	[PageContextType.ATLAS_HOME_FULL_PAGE_NOTIFICATIONS]: [],
	[PageContextType.ATLAS_HOME_PROJECT_UPDATES_LIST]: [],
	[PageContextType.ATLAS_HOME_GOAL_UPDATES_LIST]: [],
	[PageContextType.ATLAS_HOME_TEAM_UPDATES_LIST]: [],
	[PageContextType.ATLAS_HOME_TAG_UPDATES_LIST]: [],
	[PageContextType.FOCUS]: [
		{ labelType: 'summarize-content', message: messages.focusPrompt1 },
		{ labelType: 'measure-productivity', message: messages.focusPrompt2 },
	],
	[PageContextType.FOCUS_FOR_YOU]: [
		{ labelType: 'summarize-content', message: messages.focusForYouPrompt1 },
	],
	[PageContextType.FOCUS_FOCUS_AREA_PAGE]: [
		{ labelType: 'summarize-content', message: messages.focusAreaPrompt1 },
	],
	[PageContextType.FOCUS_FOCUS_AREA_GOAL_VIEW]: [
		{ labelType: 'summarize-content', message: messages.focusAreaGoalPrompt1 },
	],
	[PageContextType.FOCUS_FOCUS_AREA_WORK_VIEW]: [
		{ labelType: 'summarize-content', message: messages.focusAreaWorkPrompt1 },
	],
	[PageContextType.SAC]: [
		{ labelType: 'manage-settings', message: messages.sacPrompt1 },
		{ labelType: 'find-information', message: messages.sacPrompt2 },
		{ labelType: 'automation-rule', message: messages.sacPrompt3 },
	],
	[PageContextType.STORE_HOME]: [
		{ labelType: 'get-answers', message: messages.storeHomePrompt1 },
		{ labelType: 'find-information', message: messages.storeHomePrompt2 },
		{ labelType: 'be-productive', message: messages.storeHomePrompt3 },
	],
	[PageContextType.TRELLO_BOARD]: [
		{ labelType: 'summarize-content', message: messages.trelloBoardPrompt1 },
		{ labelType: 'be-productive', message: messages.trelloBoardPrompt2 },
		{ labelType: 'be-productive', message: messages.trelloBoardPrompt3 },
	],
	[PageContextType.TRELLO_CARD]: [
		{ labelType: 'summarize-content', message: messages.trelloCardPrompt1 },
		{ labelType: 'find-information', message: messages.trelloCardPrompt2 },
		{ labelType: 'rephrase-content', message: messages.trelloCardPrompt3 },
	],
	[PageContextType.UNKNOWN]: [
		{ labelType: 'find-information', message: messages.unknownPrompt1 },
		{ labelType: 'be-productive', message: messages.unknownPrompt2 },
		{ labelType: 'draft-content', message: messages.unknownPrompt3 },
	],
	[PageContextType.ADMIN_HUB]: [],
};

export const determinePageType = (location: string): PageContextType => {
	const jsmIncidentViewPatterns: [PageContextType, RegExp][] = fg(
		'rovo_ops_incident_conversation_starters_enabled',
	)
		? [
				// Single incident view (with issue key at the end)
				[
					PageContextType.JSM_INCIDENT_VIEW,
					/\/jira\/servicedesk\/projects\/[^\/]+\/section\/incidents\/custom\/\d+\/[A-Z]+-\d+/,
				],
			]
		: [];

	const jsmOpsPatterns: [PageContextType, RegExp][] = fg('rovo_ops_conversation_starters_enabled')
		? [
				// Incident list view (without issue key at the end)
				[
					PageContextType.JSM_INCIDENT_LIST_VIEW,
					/\/jira\/servicedesk\/projects\/[^\/]+\/section\/incidents\/custom\/\d+$/,
				],
				// Single alert view (has / after alerts)
				[PageContextType.JSM_ALERT_VIEW, /\/jira\/ops\/alerts\//],
				// Alert list view (has ? after alerts)
				[PageContextType.JSM_ALERT_LIST_VIEW, /\/jira\/ops\/alerts\?/],
				// Alert group detail view
				[PageContextType.JSM_ALERT_GROUP_DETAIL_VIEW, /\/jira\/ops\/group-detail\//],
			]
		: [];

	const isPipelinesStartersEnabled = fg('bbc-rovo-chat-pipelines-conversation-starters');

	const urlPatterns: [PageContextType, RegExp][] = [
		[PageContextType.BITBUCKET_REPOSITORY_SOURCE, /bitbucket\.org\/[^\/]+\/[^\/]+\/src\//],
		[
			isPipelinesStartersEnabled
				? PageContextType.BITBUCKET_PULL_REQUEST
				: PageContextType.BITBUCKET_PULL_REQUEST_WITHOUT_PIPELINES_STARTERS,
			/bitbucket\.org\/[^\/]+\/[^\/]+\/pull-requests\/\d+/,
		],
		[
			PageContextType.BITBUCKET_CREATE_PULL_REQUEST,
			/bitbucket\.org\/[^\/]+\/[^\/]+\/pull-requests\/new/,
		],
		// for pipelines page default to bitbucket home page starters if feature flag is off
		[
			isPipelinesStartersEnabled ? PageContextType.BITBUCKET_PIPELINES : PageContextType.BITBUCKET,
			/bitbucket\.org\/[^\/]+\/[^\/]+\/pipelines\/results\/[^\/]+\/steps\//,
		],
		[PageContextType.BITBUCKET, /bitbucket\.org\/.*/],
		[PageContextType.CONFLUENCE_HOME, /\/wiki\/?$/],
		[PageContextType.CONFLUENCE_HOME, /\/wiki\/home$/],
		[PageContextType.CONFLUENCE_BLOG_EDIT, /\/wiki\/spaces\/[^\/]+\/blog\/edit-v2\//],
		[PageContextType.CONFLUENCE_BLOG_VIEW, /\/wiki\/spaces\/[^\/]+\/blog\//],
		[PageContextType.CONFLUENCE_PAGE_EDIT, /\/wiki\/spaces\/[^\/]+\/pages\/edit-v2\//],
		[PageContextType.CONFLUENCE_PAGE_VIEW, /\/wiki\/spaces\/[^\/]+\/pages\//],
		[PageContextType.CONFLUENCE_WHITEBOARD, /\/wiki\/spaces\/[^\/]+\/whiteboard\//],
		[PageContextType.JIRA_ISSUE_VIEW, /\/browse\/[^\/]+$/],
		[PageContextType.JIRA_ISSUE_VIEW, /\/jira\/.+\/projects\/[^/]+\/[^?]+\?(?:.*&)?selectedIssue=/],
		...jsmIncidentViewPatterns,
		...jsmOpsPatterns,
		[
			PageContextType.JIRA_CMP_SPACE_SETTINGS_PEOPLE_LEGACY,
			/\/plugins\/servlet\/project-config\/[^\/]+\/people/,
		],
		[
			PageContextType.JIRA_CMP_SPACE_SETTINGS_PEOPLE_LEGACY,
			/\/plugins\/servlet\/project-config\/[^\/]+\/permissions/,
		],
		[
			PageContextType.JIRA_TMP_SPACE_SETTINGS_ACCESS,
			/\/jira\/software\/projects\/[^\/]+\/settings\/access/,
		],
		[PageContextType.JIRA_PROJECT, /\/jira\/.+\/projects\//],
		[PageContextType.JIRA_HOME, /\/jira\/dashboards\/last-visited$/],
		[PageContextType.JIRA_FOR_YOU, /\/jira\/for-you$/],
		// atlas home is either home.atlassian.com, home.stg.atlassian.com, team.atlassian.com, or team.stg.atlassian.com
		[PageContextType.ATLAS_HOME_DISCOVER, /(home|team)\..*\.com\/(?:[^\/]+\/)*discover+/],
		[PageContextType.ATLAS_HOME_RECENTS, /(home|team)\..*\.com\/(?:[^\/]+\/)*recents+/],
		[
			PageContextType.ATLAS_HOME_FULL_PAGE_NOTIFICATIONS,
			/(home|team)\..*\.com\/(?:[^\/]+\/)*notifications+/,
		],
		[
			PageContextType.ATLAS_HOME_PROJECT_UPDATES_LIST,
			/(home|team)\..*\.com\/(?:[^\/]+\/)*updates\/projects+/,
		],
		[
			PageContextType.ATLAS_HOME_GOAL_UPDATES_LIST,
			/(home|team)\..*\.com\/(?:[^\/]+\/)*updates\/goals+/,
		],
		[
			PageContextType.ATLAS_HOME_TEAM_UPDATES_LIST,
			/(home|team)\..*\.com\/(?:[^\/]+\/)*updates\/team+/,
		],
		[
			PageContextType.ATLAS_HOME_TAG_UPDATES_LIST,
			/(home|team)\..*\.com\/(?:[^\/]+\/)*updates\/tag+/,
		],
		[PageContextType.ATLAS_HOME_MAIN, /(home|team)\..*\.com\/(?:[^\/]+\/)*\?cloudId=[^\/]+/],
		[PageContextType.ATLAS_HOME, /\/(home|team)\./],
		[PageContextType.FOCUS_FOCUS_AREA_GOAL_VIEW, /\/focus\/focus-area\/.*\/goals/],
		[PageContextType.FOCUS_FOCUS_AREA_WORK_VIEW, /\/focus\/focus-area\/.*\/work/],
		[PageContextType.FOCUS_FOCUS_AREA_PAGE, /\/focus\/focus-area\/.*/],
		[PageContextType.FOCUS_FOR_YOU, /\/focus\/for-you(?:\/.*)?$/],
		[PageContextType.FOCUS, /\/focus\/.*/],
		[PageContextType.SAC, /support\.atlassian\.com/],
		// store: for now - e.g. store.stg-east.frontend.public.atl-paas.net/o/{orgId}/?cloudId=...
		[PageContextType.STORE_HOME, /store\.[^/]*atl-paas\.net/],
		// Trello: card view first (more specific), then board. Prod: trello.com, Staging: trellis.coffee (and *.atlassian.com)
		[
			PageContextType.TRELLO_CARD,
			/(?:trello\.com|trellis\.coffee|[\w-]*trello[\w-]*\.(?:stg\.)?atlassian\.com)\/c\//,
		],
		[
			PageContextType.TRELLO_BOARD,
			/(?:trello\.com|trellis\.coffee|[\w-]*trello[\w-]*\.(?:stg\.)?atlassian\.com)\/b\//,
		],
		// admin hub: admin.atlassian.com (production) or admin.stg.atlassian.com (staging)
		[PageContextType.ADMIN_HUB, /admin(\.stg)?\.atlassian\.com/],
	];

	for (const [type, pattern] of urlPatterns) {
		const matched = pattern.test(location);
		if (!matched) {
			continue;
		}

		return type;
	}

	return PageContextType.UNKNOWN;
};

export const usePageContextType = (): PageContextType => {
	const location = useWindowLocation();
	const [pageContextType, setPageContextType] = useState<PageContextType>(
		determinePageType(location),
	);

	useEffect(() => {
		setPageContextType(determinePageType(location));
	}, [location]);

	return pageContextType;
};

const shuffleStarters = (arr: ConversationAction[]): ConversationAction[] => {
	const copiedArr = [...arr];
	// Shuffle array using Fisher-Yates algorithm
	for (let i = arr.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[copiedArr[i], copiedArr[j]] = [copiedArr[j], copiedArr[i]];
	}
	return copiedArr;
};

export const getRandomStarters = (
	starters: ConversationAction[],
	num: number,
): ConversationAction[] => {
	// Return the first `num` items
	return shuffleStarters(starters).slice(0, num);
};

const getIconAndLabelForStarter = (
	labelType: ConversationStarterLabelType,
): { icon: JSX.Element; label: MessageDescriptor } => {
	switch (labelType) {
		case 'be-productive':
			return {
				icon: <ChartTrendUpIcon label="" />,
				label: messages.beProductiveLabel,
			};
		case 'stay-up-to-date':
			return {
				icon: <ChartTrendUpIcon label="" />,
				label: messages.stayUpToDateLabel,
			};
		case 'summarize-content':
			return {
				icon: <EditBulkIcon label="" />,
				label: messages.summarizeContentLabel,
			};
		case 'rephrase-content':
			return {
				icon: <EditBulkIcon label="" />,
				label: messages.rephraseContentLabel,
			};
		case 'draft-content':
			return {
				icon: <EditBulkIcon label="" />,
				label: messages.draftContentLabel,
			};
		case 'find-information':
			return {
				icon: <SearchIcon label="" />,
				label: messages.findInformationLabel,
			};
		case 'find-people':
			return {
				icon: <SearchIcon label="" />,
				label: messages.findPeopleLabel,
			};
		case 'code-search':
			return {
				icon: <SearchIcon label="" />,
				label: messages.codeSearchLabel,
			};
		case 'measure-productivity':
			return {
				icon: <ChartTrendUpIcon label="" />,
				label: messages.measureProductivityLabel,
			};
		case 'get-answers':
			return {
				icon: <SearchIcon label="" />,
				label: messages.getAnswersLabel,
			};
		case 'check-status':
			return {
				icon: <CloudArrowUpIcon label="" />,
				label: messages.checkStatusLabel,
			};
		case 'summarize-information':
			return {
				icon: <AiGenerativeTextSummaryIcon label="" />,
				label: messages.summarizeInformationLabel,
			};
		case 'analyze-content':
			return {
				icon: <AiGenerativeTextSummaryIcon label="" />,
				label: messages.analyzeContentLabel,
			};
		case 'summarize-code':
			return {
				icon: <AtomIcon label="" />,
				label: messages.summarizeCodeLabel,
			};
		case 'manage-settings':
			return {
				icon: <PersonAddedIcon label="" />,
				label: messages.manageSettingsLabel,
			};
		case 'automation-rule':
			return {
				icon: <SettingsIcon label="" />,
				label: messages.automationRuleLabel,
			};
		default:
			return {
				icon: <ChartTrendUpIcon label="" />,
				label: messages.beProductiveLabel,
			};
	}
};

// default to fetching 3 dynamic starters
const DYNAMIC_STARTERS_COUNT = 3;

interface UseRovoConversationStartersProps {
	numStarters?: number;
	dynamicStartersConfig?: {
		enabled?: boolean;
	};
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
}

interface UseRovoConversationStartersReturnType {
	conversationStarterActions: ConversationAction[];
	nestedConversationStarterActions: ConversationAction[];
	getRandomStarters: (starters: ConversationAction[], num: number) => ConversationAction[];
	isDynamicStarterActionsLoading: boolean;
	numDynamicStarterActionsLoading: number;
}

export const useRovoConversationStarters = ({
	numStarters,
	dynamicStartersConfig,
	assistanceServiceParams,
}: UseRovoConversationStartersProps): UseRovoConversationStartersReturnType => {
	const { formatMessage } = useIntl();
	const pageContextType = usePageContextType();
	const location = useWindowLocation();

	const { convoStarters: dynamicStarterActions, isLoading: isDynamicStarterActionsLoading } =
		useFetchConvoStarters({
			contentUrl: location,
			pageContextType,
			showConvoStarters: dynamicStartersConfig?.enabled && !!assistanceServiceParams,
			// hacky workaround for consumers that don't need dynamic starters (and therfore don't pass in assistanceServiceParams)
			assistanceServiceParams: assistanceServiceParams || {
				touchpointSource: '',
				product: '',
				experienceId: '',
				siteId: '',
			},
		});

	const staticStarters = useMemo(() => {
		const contextualStarters: ConversationStarterWithLabel[] = pageTypeConversationStarters[
			pageContextType
		].map((starter) => {
			return {
				labelType: starter.labelType,
				message: formatMessage(starter.message),
				type: 'static',
			};
		});

		return contextualStarters;
	}, [pageContextType, formatMessage]);

	const groupedStarters = useMemo(
		() =>
			staticStarters.reduce(
				(acc, starter) => {
					if (!starter.labelType) {
						return acc;
					}
					acc[starter.labelType] = [...(acc[starter.labelType] || []), starter];
					return acc;
				},
				{} as Record<string, typeof staticStarters>,
			),
		[staticStarters],
	);

	// Use this to get conversation starter actions that are not nested (e.g. for Sidebar)
	const conversationStarterActions: ConversationAction[] = useMemo(
		() =>
			Object.entries(groupedStarters).map(([, starters]) => {
				// If there is only one starter in the group, return as a single chat action, otherwise return one of the nested items to use as a starter
				if (starters.length === 1) {
					const starter = starters[0];
					const starterHasPlaceholder = starter.message.includes('`');
					return {
						id: 'convo-starter',
						title: starter.message,
						icon: () => <AiChatIcon label="" />,
						onClick: () => {},
						handleType: HandleActionType.Chat,
						children: [],
						...(starterHasPlaceholder
							? { dynamicPrompt: starter.message }
							: { staticPrompt: starter.message }),
						type: 'convo-starter-action' as AIActionType,
						starterType: 'static',
					};
				} else {
					//Grab a random starter from the group to use
					const randomIndex = Math.floor(Math.random() * starters.length);
					const randomStarter = starters[randomIndex];
					const randomStarterHasPlaceholder = randomStarter.message.includes('`');
					return {
						id: `convo-starter`,
						title: randomStarter.message,
						icon: () => <AiChatIcon label="" />,
						onClick: () => {},
						handleType: HandleActionType.Chat,
						...(randomStarterHasPlaceholder
							? { dynamicPrompt: randomStarter.message }
							: { staticPrompt: randomStarter.message }),
						type: 'convo-starter-action' as AIActionType,
						starterType: 'static',
					};
				}
			}),
		[groupedStarters],
	);

	// Use this to get conversation starter actions that are nested (e.g. for Full Screen)
	const nestedConversationStarterActions: NestedAction[] = useMemo(
		() =>
			Object.entries(groupedStarters).map(([, starters]) => {
				const { icon, label } = getIconAndLabelForStarter(starters[0].labelType);
				return {
					id: 'convo-starter',
					title: formatMessage(label),
					icon: () => icon,
					onClick: () => {},
					children: starters.map((starter) => {
						const starterHasPlaceholder = starter.message.includes('`');
						return {
							id: 'convo-starter',
							title: starter.message,
							icon: () => icon,
							onClick: () => {},
							type: 'convo-starter-action' as AIActionType,
							handleType: HandleActionType.Chat,
							...(starterHasPlaceholder
								? { dynamicPrompt: starter.message }
								: { staticPrompt: starter.message }),
							starterType: 'static',
						};
					}),
					handleType: HandleActionType.Nested,
					type: 'convo-starter-action' as AIActionType,
				};
			}),
		[groupedStarters, formatMessage],
	);

	const numStartersToReturn =
		numStarters ?? conversationStarterActions.length + dynamicStarterActions.length;

	// dynamic starter actions are not included in nested use case
	const numNestedStartersToReturn = numStarters ?? nestedConversationStarterActions.length;

	const randomizedConversationStarterActions = useMemo(() => {
		if (!dynamicStartersConfig?.enabled) {
			return getRandomStarters(conversationStarterActions, numStartersToReturn);
		}

		if (isDynamicStarterActionsLoading) {
			return [];
		}

		if (dynamicStarterActions.length >= numStartersToReturn) {
			return dynamicStarterActions.slice(0, numStartersToReturn);
		}

		// prioritizes dynamic starters, and then randomizes the static starters
		return [
			...dynamicStarterActions,
			...getRandomStarters(
				conversationStarterActions,
				numStartersToReturn - dynamicStarterActions.length,
			),
		];
	}, [
		conversationStarterActions,
		numStartersToReturn,
		dynamicStarterActions,
		isDynamicStarterActionsLoading,
		dynamicStartersConfig?.enabled,
	]);

	const randomizedNestedConversationStarterActions = useMemo(() => {
		return getRandomStarters(nestedConversationStarterActions, numNestedStartersToReturn);
	}, [nestedConversationStarterActions, numNestedStartersToReturn]);

	return {
		conversationStarterActions: randomizedConversationStarterActions,
		nestedConversationStarterActions: randomizedNestedConversationStarterActions,
		getRandomStarters,
		isDynamicStarterActionsLoading,
		numDynamicStarterActionsLoading: Math.min(DYNAMIC_STARTERS_COUNT, numStartersToReturn),
	};
};
